<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import Input from 'dashboard/components-next/input/Input.vue';
import SegmentedControl from 'dashboard/components-next/segmented-control/SegmentedControl.vue';

const props = defineProps({
  // ISO-ish `YYYY-MM-DDTHH:mm` string (same shape a native datetime-local
  // input produces), or empty/null to mean "send as soon as possible".
  modelValue: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

const MODE_NOW = 'now';
const MODE_SCHEDULED = 'scheduled';

const modeOptions = computed(() => [
  { value: MODE_NOW, label: t('CAMPAIGN.SCHEDULE.NOW') },
  { value: MODE_SCHEDULED, label: t('CAMPAIGN.SCHEDULE.LATER') },
]);

const mode = ref(props.modelValue ? MODE_SCHEDULED : MODE_NOW);

const nowParts = () => {
  const now = new Date(Date.now() - new Date().getTimezoneOffset() * 60000);
  return {
    date: now.toISOString().slice(0, 10),
    time: now.toISOString().slice(11, 16),
  };
};

const [initialDate, initialTime] = props.modelValue
  ? props.modelValue.split('T')
  : [nowParts().date, nowParts().time];

const dateValue = ref(initialDate);
const timeValue = ref(initialTime);

const todayDateStr = computed(() => nowParts().date);
const minTimeStr = computed(() =>
  dateValue.value === todayDateStr.value ? nowParts().time : '00:00'
);

const emitValue = () => {
  if (mode.value === MODE_NOW) {
    emit('update:modelValue', '');
    return;
  }
  emit('update:modelValue', `${dateValue.value}T${timeValue.value}`);
};

const setMode = value => {
  mode.value = value;
  if (value === MODE_SCHEDULED && (!dateValue.value || !timeValue.value)) {
    const parts = nowParts();
    dateValue.value = parts.date;
    timeValue.value = parts.time;
  }
  emitValue();
};

watch([dateValue, timeValue], emitValue);
</script>

<template>
  <div class="flex flex-col gap-2">
    <SegmentedControl
      :model-value="mode"
      :options="modeOptions"
      @update:model-value="setMode"
    />
    <div v-if="mode === MODE_SCHEDULED" class="flex gap-2">
      <Input
        v-model="dateValue"
        type="date"
        :min="todayDateStr"
        class="flex-1"
      />
      <Input v-model="timeValue" type="time" :min="minTimeStr" class="flex-1" />
    </div>
  </div>
</template>
